import React from 'react';
import clsx from 'clsx';
import { makeStyles, Grid, Typography, colors } from '@material-ui/core';
import { Page, Header } from '../../../components';

const useStyles = makeStyles((theme) => ({
  root: {
    overflow: 'hidden'
  },
  sectionWithSolidBg: {
    backgroundColor: theme.palette.vanscend.black,
    position: 'absolute',
    marginTop: '-8em',
    width: '100vw',
    height: '20em',
    clipPath: 'polygon(0 30%, 100% 0, 100% 70%, 0 100%)',
    zIndex: -1,
    [theme.breakpoints.down('xl')]: {
      marginTop: '-13em',
      height: '25em'
    },
    [theme.breakpoints.down('lg')]: {
      marginTop: '-13em',
      height: '27em'
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: '-12em',
      height: '20em'
    },
    [theme.breakpoints.down('xs')]: {
      marginTop: '-8em',
      height: '13em'
    }
  },
  impactMap: {
    position: 'relative',
    margin: '0 auto',
    zIndex: 2,
    [theme.breakpoints.down('sm')]: {
      marginTop: '5em',
      padding: '0 40px 20px'
    },
    [theme.breakpoints.down('xs')]: {
      marginTop: '2em',
      padding: '0 10px'
    }
  },
  impactGrid: {
    position: 'relative',
    marginTop: 100,
    zIndex: 1
  },
  impactGridItem: {
    color: theme.palette.vanscend.white,
    textAlign: 'center',
    paddingLeft: '3vw',
    paddingRight: '3vw',
    fontSize: '1.8em',
    marginTop: '-8vh',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.2em'
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.7em'
    }
  },
  impactGridItemFig: {
    fontWeight: 'bold',
    fontSize: '1.5em',
    display: 'block'
  },
  borderRight: {
    borderRight: '3px solid',
    borderColor: colors.grey[200]
  },
  descriptiveText: {
    color: theme.palette.vanscend.blue,
    fontWeight: 'bold',
    fontStyle: 'italic',
    lineHeight: '2em'
  },
  topicsGrid: {
    paddingTop: 150,
    paddingBottom: 50,
    position: 'relative',
    zIndex: 5
  },
  topicsImage: {
    maxWidth: 400
  },
  topicDescription: {
    width: 300,
    [theme.breakpoints.down('xs')]: {
      width: 'auto',
      textAlign: 'center',
      padding: '20px'
    }
  },
  topicDescriptionCallout: {
    fontWeight: 'bold',
    color: theme.palette.vanscend.green,
    fontSize: '1.5rem',
    textAlign: 'center',
    [theme.breakpoints.down('xs')]: {
      color: 'white'
    }
  },
  topicDescriptionMain: {
    fontSize: '1.4rem',
    textAlign: 'center'
  },
  intro: {
    marginLeft: '21vw',
    marginRight: '21vw',
    [theme.breakpoints.down('sm')]: {
      marginLeft: '5vw',
      marginRight: '5vw'
    },
    '& p': {
      marginBottom: '3vh'
    }
  },
  yearlyTopic: {
    [theme.breakpoints.down('xs')]: {
      width: '400px',
      backgroundColor: theme.palette.vanscend.green,
      margin: '-24px 0'
    }
  },
  topicsImage2: {
    [theme.breakpoints.down('xs')]: {
      margin: '-50px 0'
    }
  }
}));

const Impact = () => {
  const classes = useStyles();

  return (
    <Page title="Impact">
      <div className={classes.root}>
        <Header title="Impact" />
        <img
          className={classes.impactMap}
          src="/static/backgrounds/mapBg.svg"
        />
        <Grid container className={classes.impactGrid}>
          <div className={classes.sectionWithSolidBg} />
          <Grid item container justifyContent="center" alignItems="center">
            <Grid
              item
              className={clsx(classes.borderRight, classes.impactGridItem)}
            >
              <span className={classes.impactGridItemFig}>$345K</span>
              <span className={classes.descriptiveText}>Funding</span>
              <br /> raised to date
            </Grid>
            <Grid
              item
              className={clsx(classes.borderRight, classes.impactGridItem)}
            >
              <span className={classes.impactGridItemFig}>50+</span>
              <span className={classes.descriptiveText}>Communities</span>
              <br />
              Vanscend was introduced
            </Grid>
            <Grid item className={classes.impactGridItem}>
              <span className={classes.impactGridItemFig}>20+</span>{' '}
              <span className={classes.descriptiveText}>Years</span>
              <br /> in the making
            </Grid>
          </Grid>
        </Grid>
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
          spacing={4}
          className={classes.topicsGrid}
        >
          <Grid
            container
            item
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={6}
          >
            <Grid item>
              <img
                className={classes.topicsImage}
                src="/static/images/impact/2020/rocketman.png"
                alt="Man with rocket boosters flying over city skyline"
              />
            </Grid>
            <Grid item className={classes.yearlyTopic}>
              <Typography component="div" className={classes.topicDescription}>
                <div className={classes.topicDescriptionCallout}>
                  2020 was all about
                </div>
                <div className={classes.topicDescriptionMain}>
                  Launching in a pandemic
                </div>
              </Typography>
            </Grid>
            <Grid item>
              <img
                className={classes.topicsImage}
                src="/static/images/impact/2020/rollercoaster.png"
                alt="People riding a rollercoaster"
              />
            </Grid>
          </Grid>
          <Grid
            container
            item
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={6}
          >
            <Grid item>
              <img
                className={classes.topicsImage}
                src="/static/images/impact/2021/team.png"
                alt="Group in a circle with hands in the middle as if preparing to say 'Go Team'"
              />
            </Grid>
            <Grid item className={classes.yearlyTopic}>
              <Typography component="div" className={classes.topicDescription}>
                <div className={classes.topicDescriptionCallout}>
                  2021 was all about
                </div>
                <div className={classes.topicDescriptionMain}>
                  Building out the team and platform
                </div>
              </Typography>
            </Grid>
            <Grid item>
              <img
                className={classes.topicsImage}
                src="/static/images/impact/2021/platform.png"
                alt="Media wall with stacked monitors wrapping around the room"
              />
            </Grid>
          </Grid>
          <Grid
            container
            item
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={6}
          >
            <Grid item>
              <img
                className={classes.topicsImage}
                src="/static/images/impact/2022/teaching.png"
                alt="Teacher in front of seated students in a classroom"
              />
            </Grid>
            <Grid item className={classes.yearlyTopic}>
              <Typography component="div" className={classes.topicDescription}>
                <div className={classes.topicDescriptionCallout}>
                  2022 was all about
                </div>
                <div className={classes.topicDescriptionMain}>
                  Perfecting the curriculum
                  <br />
                  Scaling nationally
                </div>
              </Typography>
            </Grid>
            <Grid item>
              <img
                className={classes.topicsImage}
                src="/static/images/impact/2022/group.png"
                alt="Group of adults seated in a circle having a conversation"
              />
            </Grid>
          </Grid>
          <Grid
            container
            item
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={6}
          >
            <Grid item>
              <img
                className={classes.topicsImage}
                src="/static/images/impact/vanscendingA.png"
                alt="Looking out the windshield of a large camper van on a country road"
              />
            </Grid>
            <Grid item>
              <img
                className={classes.topicsImage2}
                src="/static/images/impact/a2b.png"
                alt="Digital map with waypoints A, B and C pinned and text overlay reading 2023 and beyond wherever vanscend takes us"
              />
            </Grid>
            <Grid item>
              <img
                className={classes.topicsImage}
                src="/static/images/impact/vanscendingB.png"
                alt="Man rinsing off in the back of a parked camper van"
              />
            </Grid>
          </Grid>
        </Grid>
      </div>
    </Page>
  );
};

export default Impact;
